import { ColumnsType } from 'antd/es/table';
import { EmployeeData } from '../../../types';
import { useNavigate } from 'react-router-dom';
import { Paths } from '../../../routes';
import {
  annualReviewStatuses,
  convertNameToPath,
  Statuses,
} from '../../../helper';
import { useAtom } from 'jotai';
import { userAtom } from '../../../store/atom';
import dayjs from 'dayjs';
import { formattedDateTime } from '../../../helper/functions';
import { MenteeName } from '../../hr/employee-dashboard/columns';

export const columns: ColumnsType = [
  {
    title: 'Mentee',
    dataIndex: 'name',
    key: 'name',
    sorter: (a: EmployeeData, b: EmployeeData) => {
      return a.name?.trim().localeCompare(b.name?.trim());
    },
    render: (_, record: EmployeeData) => (
      <MenteeName
        data={{ ...record }}
        path={`${Paths.AllMentees}/${convertNameToPath(record.name)}`}
        pathSate={{ userId: record.id }}
      />
    ),
  },
  {
    title: 'Designation',
    dataIndex: 'designation',
    key: 'designation',
    sorter: (a: EmployeeData, b: EmployeeData) => {
      return a.designation?.trim().localeCompare(b.designation?.trim());
    },
  },
  {
    title: <div className="text-center">Tier</div>,
    dataIndex: 'tier',
    key: 'tier',
    sorter: (a: EmployeeData, b: EmployeeData) =>
      Number(a.tier) - Number(b.tier),
    render: (tier) => <div className="text-center">{tier}</div>,
  },
  {
    title: <div className="text-center">No. Of Objectives Set</div>,
    dataIndex: 'objectiveCount',
    key: 'objectiveCount',
    sorter: (a: EmployeeData, b: EmployeeData) =>
      Number(a.objectiveCount) - Number(b.objectiveCount),
    render: (objectiveCount) => (
      <div className="text-center">{objectiveCount}</div>
    ),
  },
  {
    title: 'Current Quarterly Review',
    key: 'cycleId',
    render: (data: EmployeeData) => <QuarterName data={data} />,
  },
  {
    title: 'Annual Reviews Involved In',
    key: 'annualData',
    render: (data: EmployeeData) => (
      <div className="flex justify-start items-center gap-1">
        {Number(data.tier) === 6 ? (
          <div className="text-center">N/A</div>
        ) : !data.annualData || data.annualData.length === 0 ? (
          <div className="text-center">-</div>
        ) : (
          data.annualData.map((item: any, idx: number) => (
            <AnnualCycle
              key={idx}
              data={data}
              year={item.year}
              status={item.status}
              annualCycleId={item.id}
            />
          ))
        )}
      </div>
    ),
  },
  {
    title: <div className="text-center">Last Accessed</div>,
    dataIndex: 'lastAccessedDate',
    key: 'lastAccessedDate',
    sorter: (a: EmployeeData, b: EmployeeData) => {
      if (a.lastAccessedDate === null && b.lastAccessedDate === null) {
        return 0;
      }
      if (a.lastAccessedDate === null) {
        return 1; // Treat null as the largest value, so it appears last
      }
      if (b.lastAccessedDate === null) {
        return -1; // Treat null as the largest value, so it appears last
      }
      return dayjs(a.lastAccessedDate.trim()).isBefore(
        dayjs(b.lastAccessedDate.trim())
      )
        ? -1
        : 1;
    },
    render: (lastAccessedDate) =>
      lastAccessedDate ? (
        <div className="text-left">{formattedDateTime(lastAccessedDate)}</div>
      ) : (
        <div className="text-center">-</div>
      ),
  },
];

export const QuarterName = ({ data }: { data: EmployeeData }) => {
  const [userState, setUserState] = useAtom(userAtom);
  const navigate = useNavigate();

  const handleClick = () => {
    setUserState({ ...userState, selectedMentee: data });
    navigate(
      `${Paths.QuarterlyReview}/${convertNameToPath(data?.name || '')}`,
      { state: { from: 'mentor' } }
    );
  };

  const currentCycle = data.quarterData?.find(
    (item: any) => annualReviewStatuses[item.status] === Statuses.InProgress
  );

  if (!data.cycleName || !data.cycleId || !currentCycle) {
    return <div className="text-center">-</div>;
  }

  const startDate = dayjs(currentCycle.startDate);

  const quarter = Math.ceil((startDate.month() + 1) / 4);
  const year = startDate.year();

  return (
    <div
      onClick={handleClick}
      className="text-left text-blue-600 underline cursor-pointer"
    >
      {`Q${quarter} - ${year}`}
    </div>
  );
};

export const AnnualCycle = ({
  data,
  year,
  annualCycleId,
  status,
}: {
  data: EmployeeData;
  year: string;
  annualCycleId: string;
  status: number;
}) => {
  const [userState, setUserState] = useAtom(userAtom);
  const navigate = useNavigate();

  const handleClick = () => {
    setUserState({
      ...userState,
      selectedMentee: data,
      cycleId: annualCycleId,
      quarterId: data.cycleId,
    });

    navigate(
      `${Paths.AllMentees}/${convertNameToPath(data?.name || '')}/annual-feedback`,
      { state: { from: 'mentor' } }
    );
  };

  return (
    <div
      onClick={handleClick}
      className="text-left text-blue-600 underline cursor-pointer"
    >
      {year}
      {annualReviewStatuses[status] === Statuses.InProgress
        ? ' (In Progress)'
        : ''}
    </div>
  );
};
